import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

import { getEmbedThemeParam, isEmbedPath } from 'utils/embedBoot';

type Theme = 'dark' | 'light' | 'system';

const embedThemeOverride = (): Theme | null => {
  if (typeof window === 'undefined' || !isEmbedPath()) return null;
  return getEmbedThemeParam(window.location.search);
};

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'dark' | 'light';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'themeNew',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () =>
      embedThemeOverride() ||
      (localStorage.getItem(storageKey) as Theme) ||
      defaultTheme,
  );

  // Separate state for the resolved theme (actual theme applied)
  const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(
    theme === 'dark' ? 'dark' : 'light',
  );

  const forcedTheme = useMemo(() => embedThemeOverride(), []);

  // Function to get system theme
  const getSystemTheme = (): 'dark' | 'light' => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  };

  // Update resolvedTheme whenever theme changes or system preferences change
  useEffect(() => {
    // Set initial resolved theme
    if (theme === 'system') {
      setResolvedTheme(getSystemTheme());

      // Set up listener for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (event: MediaQueryListEvent) => {
        setResolvedTheme(event.matches ? 'dark' : 'light');
      };

      // Modern event listener approach
      mediaQuery.addEventListener('change', handleChange);

      // Cleanup listener when component unmounts
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // If theme is explicitly set to 'dark' or 'light', use that
      setResolvedTheme(theme as 'dark' | 'light');
    }
  }, [theme]); // Only re-run if theme preference changes

  // Apply theme to document and save to localStorage
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove both theme classes
    root.classList.remove('light', 'dark');

    // Add the resolved theme class
    root.classList.add(resolvedTheme);

    if (!forcedTheme) localStorage.setItem(storageKey, theme);
    const metaThemeColor = document.getElementById('theme-color-meta');

    const color = resolvedTheme === 'dark' ? '#111b21' : '#fffffe';
    if (metaThemeColor) metaThemeColor.setAttribute('content', color);

    // Update native status bar and navigation bar
    if (Capacitor.isNativePlatform()) {
      const updateSystemBars = async () => {
        const isDark = resolvedTheme === 'dark';
        const bgColor = isDark ? '#111b21' : '#ffffff';

        // Update Status Bar
        try {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setBackgroundColor({ color: bgColor });
          await StatusBar.setStyle({
            style: isDark ? Style.Light : Style.Dark,
          });
        } catch (e) {
          if (import.meta.env.DEV) console.error('StatusBar error:', e);
        }
      };

      // Run immediately and also after a small delay for reliability
      updateSystemBars();
      setTimeout(updateSystemBars, 300);
    }
  }, [resolvedTheme, theme, storageKey, forcedTheme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => setTheme(newTheme),
    resolvedTheme,
  };

  return (
    <ThemeContext.Provider value={value} {...props}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
